import { Button, Callout, Icon } from "@blueprintjs/core"
import { useCallback, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useApi } from "../../hooks/useApi"
import { SCIENT_ROUTES } from "../../Routes"
import {
  OAuthConsentContainer,
  OAuthCard,
  Title,
  ErrorTitle,
  SubText,
  ButtonRow,
  DetailsList,
  DetailRow,
} from "./OAuthConsent.styled"
import { ScientLogoNew } from "../../components/ScientLogo/ScientLogoNew"

/**
 * OAuth Consent Page for MCP clients.
 * URL: /oauth/consent?client_id=...&redirect_uri=...&code_challenge=...&scope=...&state=...
 */
const OAuthConsent = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { scientApi } = useApi()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const oauthParams = useMemo(
    () => ({
      clientId: searchParams.get("client_id") || "",
      redirectUri: searchParams.get("redirect_uri") || "",
      responseType: searchParams.get("response_type") || "code",
      scope: searchParams.get("scope") || "",
      state: searchParams.get("state") || undefined,
      codeChallenge: searchParams.get("code_challenge") || "",
    }),
    [searchParams],
  )

  const isValidRequest = useMemo(() => {
    return (
      oauthParams.clientId &&
      oauthParams.redirectUri &&
      oauthParams.codeChallenge &&
      oauthParams.responseType === "code"
    )
  }, [oauthParams])

  const handleApprove = useCallback(async () => {
    if (!scientApi) return

    setIsSubmitting(true)
    setError(null)

    try {
      const request: Parameters<typeof scientApi.auth.oauthAuthorize>[0] = {
        client_id: oauthParams.clientId,
        redirect_uri: oauthParams.redirectUri,
        response_type: oauthParams.responseType,
        scope: oauthParams.scope,
        code_challenge: oauthParams.codeChallenge,
        code_challenge_method: "S256",
      }
      if (oauthParams.state) {
        request.state = oauthParams.state
      }

      const response = await scientApi.auth.oauthAuthorize(request)
      window.location.href = response.redirect_uri
    } catch (err: any) {
      console.error("OAuth authorize error:", err)
      setError(err.response?.data?.error_description || "Authorization failed. Please try again.")
      setIsSubmitting(false)
    }
  }, [scientApi, oauthParams])

  const handleDeny = useCallback(() => {
    navigate(SCIENT_ROUTES.HOME)
  }, [navigate])

  if (!isValidRequest) {
    return (
      <OAuthConsentContainer alignItems="center" justifyContent="center">
        <OAuthCard flexDirection="column" gap="20px" alignItems="center">
          <ScientLogoNew height="50px" />
          <ErrorTitle>Invalid Authorization Request</ErrorTitle>
          <Callout intent="danger">
            Missing required parameters. Please check the authorization URL.
          </Callout>
          <Button intent="primary" onClick={() => navigate(SCIENT_ROUTES.HOME)}>
            Go to Home
          </Button>
        </OAuthCard>
      </OAuthConsentContainer>
    )
  }

  return (
    <OAuthConsentContainer alignItems="center" justifyContent="center">
      <OAuthCard flexDirection="column" gap="25px" alignItems="center">
        <ScientLogoNew height="50px" />
        <Icon icon="unlock" size={40} color="#48aff0" />
        <Title>Authorize MCP Client</Title>
        <SubText>An application is requesting access to your Scient account</SubText>

        <DetailsList>
          <DetailRow>
            <span>Redirect to</span>
            <span>{oauthParams.redirectUri}</span>
          </DetailRow>
          <DetailRow>
            <span>Scope</span>
            <span>{oauthParams.scope || "mcp"}</span>
          </DetailRow>
          <DetailRow>
            <span>Client ID</span>
            <span>{oauthParams.clientId.slice(0, 8)}...</span>
          </DetailRow>
        </DetailsList>

        {error && <Callout intent="danger">{error}</Callout>}

        <ButtonRow>
          <Button large intent="none" onClick={handleDeny} disabled={isSubmitting} fill>
            Deny
          </Button>
          <Button large intent="primary" onClick={handleApprove} loading={isSubmitting} fill>
            Approve
          </Button>
        </ButtonRow>
      </OAuthCard>
    </OAuthConsentContainer>
  )
}

export default OAuthConsent
